(() => {
  const COOKIE_NAME = 'abtest_active_groups';
  const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
  const COOKIE_MAX_LENGTH = 3800;
  const CARD_SELECTOR = '[data-main-product]';
  const CONFIG_SELECTOR = '[data-abtest-management]';
  const PRODUCT_HANDLER_SELECTOR = '[data-product-handler]';
  const VARIANT_CHANGED_EVENT = 'abtest:variant-changed';
  const RESOLVED_EVENT = 'abtest:resolved';
  const LEGACY_HSW5_TRIGGERS = new Set(['control', 'test-with-dock', 'watch-only', 'test-watch-only']);

  let configuration = emptyConfiguration();
  let activeState = {};
  let configurationReady = false;
  let initialized = false;
  let resolvedAddOnRules = [];
  let resolvedAlternativeMap = new Map();
  let pendingTriggers = [];

  function emptyConfiguration() {
    return {
      experiments: [],
      experimentIndex: new Map(),
      triggerIndex: new Map(),
      productHandler: {
        addOns: [],
        alternatives: []
      }
    };
  }

  function normalize(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  function normalizeRedirectPath(value) {
    const rawValue = String(value == null ? '' : value).trim();
    if (!rawValue) return '';

    try {
      const pathname = new URL(rawValue, window.location.origin).pathname || '/';
      return pathname !== '/' ? pathname.replace(/\/+$/, '') : '/';
    } catch (error) {
      const pathname = rawValue.split(/[?#]/, 1)[0].trim();
      if (!pathname) return '';
      const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
      return normalizedPath !== '/' ? normalizedPath.replace(/\/+$/, '') : '/';
    }
  }

  function normalizeVariantId(value) {
    const variantId = String(value == null ? '' : value).trim();
    return /^\d+$/.test(variantId) && Number(variantId) > 0 ? variantId : '';
  }

  function uniqueVariantIds(values) {
    return [...new Set((Array.isArray(values) ? values : [])
      .map(normalizeVariantId)
      .filter(Boolean))];
  }

  function parseJson(element, fallback) {
    if (!element?.textContent) return fallback;

    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[A/B Test] Invalid JSON configuration:', error);
      return fallback;
    }
  }

  function readCookieValue(name) {
    const prefix = `${name}=`;
    const match = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(prefix));

    return match ? match.slice(prefix.length) : '';
  }

  function readCookieState() {
    const rawValue = readCookieValue(COOKIE_NAME);
    if (!rawValue) return {};

    try {
      const parsed = JSON.parse(decodeURIComponent(rawValue));
      if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') return {};

      return Object.fromEntries(Object.entries(parsed)
        .map(([experimentName, trigger]) => [String(experimentName).trim(), String(trigger).trim()])
        .filter(([experimentName, trigger]) => experimentName && trigger));
    } catch (error) {
      console.warn('[A/B Test] Invalid active-group cookie. Resetting it.', error);
      return {};
    }
  }

  function writeCookieState(state) {
    const entries = Object.entries(state).filter(([experimentName, trigger]) => (
      String(experimentName).trim() && String(trigger).trim()
    ));

    if (!entries.length) {
      document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
      return;
    }

    const value = encodeURIComponent(JSON.stringify(Object.fromEntries(entries)));
    if (value.length > COOKIE_MAX_LENGTH) {
      console.warn('[A/B Test] Active-group cookie is too large and was not written.');
      return;
    }

    document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
  }

  function parseAddOnRule(element) {
    if (!element) return null;

    const targetProductId = String(element.dataset.targetProductId || '').trim();
    const targetProductHandle = String(element.dataset.targetProductHandle || '').trim();
    if (!targetProductId && !targetProductHandle) return null;

    const targetVariantIds = uniqueVariantIds(parseJson(
      element.querySelector('[data-target-variant-ids]'),
      []
    ));
    const addOnProducts = parseJson(element.querySelector('[data-addon-variants]'), []);
    const addOnVariantIds = uniqueVariantIds((Array.isArray(addOnProducts) ? addOnProducts : [])
      .filter((item) => item && item.available !== false)
      .map((item) => item.variantId ?? item.id));

    return {
      targetProductId,
      targetProductHandle,
      targetVariantIds,
      addOnVariantIds
    };
  }

  function parseAlternativeRule(element) {
    if (!element) return null;

    const targetVariantId = normalizeVariantId(element.dataset.targetVariantId);
    const alternativeVariantId = normalizeVariantId(element.dataset.alternativeVariantId);
    if (!targetVariantId || !alternativeVariantId || targetVariantId === alternativeVariantId) return null;

    return {
      targetVariantId,
      alternativeVariantId,
      targetProductId: String(element.dataset.targetProductId || '').trim(),
      targetProductHandle: String(element.dataset.targetProductHandle || '').trim(),
      alternativeProductId: String(element.dataset.alternativeProductId || '').trim(),
      alternativeProductHandle: String(element.dataset.alternativeProductHandle || '').trim()
    };
  }

  function parseRules(scope) {
    if (!scope) return { addOns: [], alternatives: [] };

    return {
      addOns: Array.from(scope.querySelectorAll('[data-addon-rule]'))
        .map(parseAddOnRule)
        .filter(Boolean),
      alternatives: Array.from(scope.querySelectorAll('[data-alternative-rule]'))
        .map(parseAlternativeRule)
        .filter(Boolean)
    };
  }

  function refreshConfiguration() {
    const next = emptyConfiguration();
    const experimentNameConflicts = new Set();
    const triggerConflicts = new Set();
    const managementRoots = Array.from(document.querySelectorAll(CONFIG_SELECTOR));

    managementRoots.forEach((root) => {
      root.querySelectorAll('[data-abtest-experiment]').forEach((experimentElement) => {
        const name = String(experimentElement.dataset.abtestExperimentName || '').trim();
        const normalizedName = normalize(name);
        if (!normalizedName) return;

        const experiment = {
          name,
          normalizedName,
          groups: []
        };

        experimentElement.querySelectorAll('[data-abtest-group]').forEach((groupElement) => {
          const trigger = String(groupElement.dataset.abtestTrigger || '').trim();
          const normalizedTrigger = normalize(trigger);
          if (!normalizedTrigger) return;

          const rules = parseRules(groupElement);
          const group = {
            experimentName: name,
            normalizedExperimentName: normalizedName,
            name: String(groupElement.dataset.abtestGroupName || '').trim() || trigger,
            trigger,
            normalizedTrigger,
            redirectFrom: String(groupElement.dataset.abtestRedirectFrom || '').trim(),
            redirectTemplate: String(groupElement.dataset.abtestRedirectTemplate || '').trim(),
            addOns: rules.addOns,
            alternatives: rules.alternatives
          };

          experiment.groups.push(group);

          if (next.triggerIndex.has(normalizedTrigger)) {
            triggerConflicts.add(normalizedTrigger);
            next.triggerIndex.set(normalizedTrigger, null);
          } else {
            next.triggerIndex.set(normalizedTrigger, group);
          }
        });

        if (next.experimentIndex.has(normalizedName)) {
          experimentNameConflicts.add(normalizedName);
          next.experimentIndex.set(normalizedName, null);
        } else {
          next.experimentIndex.set(normalizedName, experiment);
        }

        next.experiments.push(experiment);
      });
    });

    experimentNameConflicts.forEach((experimentName) => {
      console.warn(`[A/B Test] Duplicate experiment name found: ${experimentName}. Rename experiments so each name is unique.`);
    });

    triggerConflicts.forEach((trigger) => {
      console.warn(`[A/B Test] Duplicate group trigger found: ${trigger}. setVariant() triggers must be unique across all experiments.`);
    });

    const productHandlerRoot = document.querySelector(PRODUCT_HANDLER_SELECTOR);
    next.productHandler = parseRules(productHandlerRoot);

    configuration = next;
    // Before DOMContentLoaded the management section may not exist yet because
    // this asset is loaded from <head>. Once DOM parsing is complete, an
    // absent section is a valid empty configuration and stale cookie state
    // should be removed.
    configurationReady = managementRoots.length > 0 || document.readyState !== 'loading';
  }

  function validateState(state) {
    const validated = {};

    Object.entries(state || {}).forEach(([storedExperimentName, storedTrigger]) => {
      const experiment = configuration.experimentIndex.get(normalize(storedExperimentName));
      if (!experiment) return;

      const group = experiment.groups.find((candidate) => (
        candidate.normalizedTrigger === normalize(storedTrigger)
      ));
      if (!group) return;
      if (configuration.triggerIndex.get(group.normalizedTrigger) !== group) return;

      validated[experiment.name] = group.trigger;
    });

    return validated;
  }

  function stateSignature(state) {
    return JSON.stringify(Object.entries(state || {})
      .map(([experimentName, trigger]) => [normalize(experimentName), normalize(trigger)])
      .sort(([firstName], [secondName]) => firstName.localeCompare(secondName)));
  }

  function getActiveGroups() {
    return Object.entries(activeState).map(([experimentName, trigger]) => {
      const experiment = configuration.experimentIndex.get(normalize(experimentName));
      if (!experiment) return null;
      return experiment.groups.find((group) => group.normalizedTrigger === normalize(trigger)) || null;
    }).filter(Boolean);
  }

  function syncCompatibilityGlobals(preferredTrigger) {
    const activeTriggers = Object.values(activeState);
    window.ABTEST_VARIANTS = activeTriggers.slice();

    const normalizedPreferred = normalize(preferredTrigger);
    if (normalizedPreferred && activeTriggers.some((trigger) => normalize(trigger) === normalizedPreferred)) {
      window.ABTEST_VARIANT = preferredTrigger;
      return;
    }

    if (activeTriggers.some((trigger) => normalize(trigger) === normalize(window.ABTEST_VARIANT))) return;
    window.ABTEST_VARIANT = activeTriggers.length === 1 ? activeTriggers[0] : (activeTriggers.at(-1) || undefined);
  }

  function setCardVisibility(card, isVisible) {
    card.toggleAttribute('hidden', !isVisible);
    card.toggleAttribute('inert', !isVisible);
    card.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
  }

  function resetCards() {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (!card.hasAttribute('data-abtest-base-hidden')) {
        card.dataset.abtestBaseHidden = card.hasAttribute('hidden') ? 'true' : 'false';
      }
      setCardVisibility(card, card.dataset.abtestBaseHidden !== 'true');
    });
  }

  function cardMatchesTrigger(card, normalizedTrigger) {
    if (!normalizedTrigger) return false;
    return normalize(card.dataset.trigger).split(/[\s,]+/).filter(Boolean).includes(normalizedTrigger);
  }

  function applyCardGroup(group) {
    const cards = Array.from(document.querySelectorAll(CARD_SELECTOR));
    const activeTrigger = group.normalizedTrigger;
    const matchingCards = cards.filter((card) => cardMatchesTrigger(card, activeTrigger));

    if (!matchingCards.length && activeTrigger === 'control') {
      const triggeredMainProducts = new Set(cards
        .filter((card) => String(card.dataset.trigger || '').trim())
        .map((card) => card.dataset.mainProduct)
        .filter(Boolean));

      cards.forEach((card) => {
        if (!card.hasAttribute('data-trigger') && triggeredMainProducts.has(card.dataset.mainProduct)) {
          matchingCards.push(card);
        }
      });
    }

    const matchedMainProducts = new Set(matchingCards
      .map((card) => card.dataset.mainProduct)
      .filter(Boolean));
    if (!matchedMainProducts.size) return;

    cards.forEach((card) => {
      if (matchedMainProducts.has(card.dataset.mainProduct)) setCardVisibility(card, false);
    });
    matchingCards.forEach((card) => setCardVisibility(card, true));
  }

  function addOnRuleKey(rule) {
    if (rule.targetProductId) return `id:${rule.targetProductId}`;
    return `handle:${normalize(rule.targetProductHandle)}`;
  }

  function mergeAddOnRules(rules) {
    const merged = new Map();

    rules.forEach((rule) => {
      const key = addOnRuleKey(rule);
      if (!key || key === 'handle:') return;

      const current = merged.get(key) || {
        ...rule,
        targetVariantIds: [],
        addOnVariantIds: []
      };
      current.targetVariantIds = uniqueVariantIds(current.targetVariantIds.concat(rule.targetVariantIds));
      current.addOnVariantIds = uniqueVariantIds(current.addOnVariantIds.concat(rule.addOnVariantIds));
      merged.set(key, current);
    });

    return merged;
  }

  function resolveAddOnRules(activeGroups) {
    const resolved = mergeAddOnRules(configuration.productHandler.addOns);
    const abBuckets = new Map();

    activeGroups.forEach((group) => {
      group.addOns.forEach((rule) => {
        const key = addOnRuleKey(rule);
        const bucket = abBuckets.get(key) || {
          experiments: new Set(),
          rules: []
        };
        bucket.experiments.add(group.normalizedExperimentName);
        bucket.rules.push(rule);
        abBuckets.set(key, bucket);
      });
    });

    abBuckets.forEach((bucket, key) => {
      if (bucket.experiments.size > 1) {
        console.warn(`[A/B Test] Multiple active experiments define add-ons for ${key}. Keeping Product Handler behavior for this target.`);
        return;
      }

      const groupRules = mergeAddOnRules(bucket.rules);
      if (groupRules.has(key)) resolved.set(key, groupRules.get(key));
    });

    return Array.from(resolved.values());
  }

  function resolveAlternativeRules(activeGroups) {
    const resolved = new Map();

    configuration.productHandler.alternatives.forEach((rule) => {
      resolved.set(rule.targetVariantId, rule.alternativeVariantId);
    });

    const abBuckets = new Map();
    activeGroups.forEach((group) => {
      group.alternatives.forEach((rule) => {
        const bucket = abBuckets.get(rule.targetVariantId) || {
          experiments: new Set(),
          alternatives: new Set()
        };
        bucket.experiments.add(group.normalizedExperimentName);
        bucket.alternatives.add(rule.alternativeVariantId);
        abBuckets.set(rule.targetVariantId, bucket);
      });
    });

    abBuckets.forEach((bucket, targetVariantId) => {
      if (bucket.experiments.size > 1 || bucket.alternatives.size > 1) {
        console.warn(`[A/B Test] Multiple active experiments define alternatives for variant ${targetVariantId}. Keeping Product Handler behavior for this variant.`);
        return;
      }
      resolved.set(targetVariantId, Array.from(bucket.alternatives)[0]);
    });

    return resolved;
  }

  function getFormProductContext(form) {
    const productRoot = form.closest('[data-product-handle]');
    const handle = String(form.dataset.productHandle || productRoot?.dataset.productHandle || '').trim();
    const jsonElement = form.querySelector('[data-product-json]')
      || productRoot?.querySelector('[data-product-json]')
      || form.parentElement?.querySelector('[data-product-json]');
    const productData = parseJson(jsonElement, null);

    return {
      id: String(productData?.id || '').trim(),
      handle
    };
  }

  function getFormAddOnElement(form, create = false) {
    let element = form.querySelector('[data-product-add-ons]');
    if (!element && create) {
      element = document.createElement('script');
      element.type = 'application/json';
      element.setAttribute('data-product-add-ons', '');
      element.textContent = '[]';
      form.prepend(element);
    }
    return element;
  }

  function ensureBaseAddOns(element) {
    if (!element) return [];

    if (!element.hasAttribute('data-abtest-base-add-ons')) {
      element.dataset.abtestBaseAddOns = JSON.stringify(uniqueVariantIds(parseJson(element, [])));
    }

    try {
      return uniqueVariantIds(JSON.parse(element.dataset.abtestBaseAddOns || '[]'));
    } catch (error) {
      return [];
    }
  }

  function findAddOnRuleForForm(form) {
    const context = getFormProductContext(form);
    return resolvedAddOnRules.find((rule) => (
      (rule.targetProductId && context.id && rule.targetProductId === context.id)
      || (rule.targetProductHandle && context.handle && normalize(rule.targetProductHandle) === normalize(context.handle))
    )) || null;
  }

  function applyResolvedAddOnsToForm(form) {
    let dataElement = getFormAddOnElement(form, false);
    if (dataElement) ensureBaseAddOns(dataElement);

    const rule = findAddOnRuleForForm(form);
    if (!rule) {
      if (dataElement) dataElement.textContent = JSON.stringify(ensureBaseAddOns(dataElement));
      return;
    }

    dataElement = dataElement || getFormAddOnElement(form, true);
    ensureBaseAddOns(dataElement);
    dataElement.textContent = JSON.stringify(rule.addOnVariantIds);
  }

  function applyResolvedAddOns() {
    document.querySelectorAll('[data-product-form]').forEach(applyResolvedAddOnsToForm);
  }

  function releasePendingButtons() {
    document.querySelectorAll('[data-hsw5-ab-pending]').forEach((button) => {
      button.removeAttribute('data-hsw5-ab-pending');
      button.removeAttribute('aria-busy');
      button.disabled = button.dataset.variantAvailable !== 'true';
    });
  }

  function emitState(source, changedGroup) {
    const activeGroups = getActiveGroups();
    const groupsToEmit = changedGroup ? [changedGroup] : activeGroups;

    groupsToEmit.forEach((group) => {
      window.dispatchEvent(new CustomEvent(VARIANT_CHANGED_EVENT, {
        detail: {
          source,
          testVariant: group.trigger,
          trigger: group.trigger,
          experiment: group.experimentName,
          experimentName: group.experimentName,
          groupName: group.name,
          normalizedTrigger: group.normalizedTrigger,
          groupLabel: group.name
        }
      }));
    });

    window.ABTEST_RESOLVED = true;
    window.dispatchEvent(new CustomEvent(RESOLVED_EVENT, {
      detail: {
        source,
        state: { ...activeState },
        activeGroups: activeGroups.map((group) => ({
          experiment: group.experimentName,
          group: group.name,
          trigger: group.trigger
        }))
      }
    }));
  }

  function applyGroupRedirects(activeGroups) {
    const currentPath = normalizeRedirectPath(window.location.pathname);
    if (!currentPath) return false;

    for (const group of activeGroups) {
      const redirectFrom = normalizeRedirectPath(group.redirectFrom);
      const redirectTemplate = String(group.redirectTemplate || '').trim();
      if (!redirectFrom || !redirectTemplate || redirectFrom !== currentPath) continue;

      const target = new URL(window.location.href);
      if (target.searchParams.get('view') === redirectTemplate) continue;

      target.searchParams.set('view', redirectTemplate);
      window.location.replace(target.toString());
      return true;
    }

    return false;
  }

  function applyActiveState(source = 'manual', changedGroup = null) {
    const activeGroups = getActiveGroups();

    if (applyGroupRedirects(activeGroups)) {
      syncCompatibilityGlobals(changedGroup?.trigger);
      emitState(source, changedGroup);
      return;
    }

    resetCards();
    activeGroups.forEach(applyCardGroup);

    resolvedAddOnRules = resolveAddOnRules(activeGroups);
    resolvedAlternativeMap = resolveAlternativeRules(activeGroups);
    applyResolvedAddOns();
    syncCompatibilityGlobals(changedGroup?.trigger);
    releasePendingButtons();
    emitState(source, changedGroup);
  }

  function getConfiguredGroupByTrigger(trigger) {
    const normalizedTrigger = normalize(trigger);
    if (!normalizedTrigger) return null;
    if (!configuration.triggerIndex.has(normalizedTrigger)) return null;

    const group = configuration.triggerIndex.get(normalizedTrigger);
    if (!group) {
      console.warn(`[A/B Test] Ignored ambiguous setVariant() trigger: ${trigger}`);
      return null;
    }

    return group;
  }

  function stateContainsTrigger(state, trigger) {
    const normalizedTrigger = normalize(trigger);
    return Object.values(state || {}).some((activeTrigger) => normalize(activeTrigger) === normalizedTrigger);
  }

  function setVariant(testVariant) {
    const trigger = String(testVariant == null ? '' : testVariant).trim();
    if (!normalize(trigger)) {
      console.warn('[A/B Test] Ignored empty setVariant() trigger:', testVariant);
      return;
    }

    if (!configurationReady) {
      if (!pendingTriggers.some((pendingTrigger) => normalize(pendingTrigger) === normalize(trigger))) {
        pendingTriggers.push(trigger);
      }
      return;
    }

    const currentCookieState = validateState(readCookieState());
    if (stateContainsTrigger(currentCookieState, trigger)) return;

    const group = getConfiguredGroupByTrigger(trigger);
    if (!group) return;

    const nextState = validateState(readCookieState());
    nextState[group.experimentName] = group.trigger;
    activeState = validateState(nextState);
    writeCookieState(activeState);
    syncCompatibilityGlobals(group.trigger);
    applyActiveState('setVariant', group);
  }

  function getVariant(experimentName) {
    const normalizedExperimentName = normalize(experimentName);
    const storedEntry = Object.entries(activeState).find(([name]) => normalize(name) === normalizedExperimentName);
    if (storedEntry) return storedEntry[1];

    // Transitional compatibility for the existing HSW5 CartManager route.
    if (normalizedExperimentName === 'hsw5') {
      return Object.values(activeState).find((trigger) => LEGACY_HSW5_TRIGGERS.has(normalize(trigger)));
    }

    return undefined;
  }

  function getGroupLabel(trigger) {
    return getConfiguredGroupByTrigger(trigger)?.name || String(trigger == null ? '' : trigger).trim();
  }

  function prepareAlternativeSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;

    // Forms can be injected after page load. Resolve their add-on data at the
    // last safe point before CartManager serializes the form.
    applyResolvedAddOnsToForm(form);
    if (!resolvedAlternativeMap.size) return;

    const inputs = Array.from(form.querySelectorAll('input[name="id"], input[name^="items["][name$="[id]"]'));
    if (!inputs.length) return;

    const changedInputs = [];
    const hadUseFormVariantId = form.hasAttribute('data-use-form-variant-id');

    inputs.forEach((input) => {
      let sourceVariantId = normalizeVariantId(input.value);
      if (
        input.name === 'id'
        && !hadUseFormVariantId
        && typeof window.variantManager?.getCurrentVariantId === 'function'
      ) {
        sourceVariantId = normalizeVariantId(window.variantManager.getCurrentVariantId()) || sourceVariantId;
      }

      const alternativeVariantId = resolvedAlternativeMap.get(sourceVariantId);
      if (!alternativeVariantId) return;

      changedInputs.push({ input, value: input.value });
      input.value = alternativeVariantId;
    });

    if (!changedInputs.length) return;

    form.setAttribute('data-use-form-variant-id', '');
    queueMicrotask(() => {
      changedInputs.forEach(({ input, value }) => {
        if (input.isConnected) input.value = value;
      });
      if (!hadUseFormVariantId) form.removeAttribute('data-use-form-variant-id');
    });
  }

  function refreshFromDom(source = 'refresh') {
    refreshConfiguration();
    if (!configurationReady) return;

    const rawState = readCookieState();
    const validatedState = validateState(rawState);
    const cookieChanged = stateSignature(rawState) !== stateSignature(validatedState);

    activeState = validatedState;
    if (cookieChanged) writeCookieState(activeState);
    applyActiveState(source);
  }

  function processPendingTriggers() {
    const queuedTriggers = pendingTriggers.slice();
    pendingTriggers = [];
    queuedTriggers.forEach((trigger) => setVariant(trigger));
  }

  function initialize() {
    refreshFromDom('page-load');
    initialized = true;
    processPendingTriggers();

    window.setTimeout(() => {
      if (!window.ABTEST_RESOLVED) {
        window.ABTEST_RESOLVED = true;
        releasePendingButtons();
        window.dispatchEvent(new CustomEvent(RESOLVED_EVENT, {
          detail: { source: 'fallback', state: { ...activeState }, activeGroups: [] }
        }));
      } else {
        releasePendingButtons();
      }
    }, 2000);
  }

  window.ABTEST_RESOLVED = window.ABTEST_RESOLVED === true;
  window.setVariant = setVariant;
  window.setVariant.abtestHandler = true;

  window.HSW5_AB = window.HSW5_AB || {};
  window.HSW5_AB.setVariant = () => undefined;

  window.ABTestHandler = {
    apply: (testVariant) => {
      if (typeof testVariant !== 'undefined') return setVariant(testVariant);
      applyActiveState('manual');
    },
    refresh: () => refreshFromDom('manual-refresh'),
    getState: () => ({ ...activeState }),
    getActiveGroups: () => getActiveGroups().map((group) => ({
      experiment: group.experimentName,
      group: group.name,
      trigger: group.trigger,
      redirectFrom: group.redirectFrom,
      redirectTemplate: group.redirectTemplate
    })),
    getGroupLabel,
    getVariant,
    hasVariant: (trigger) => stateContainsTrigger(activeState, trigger),
    isResolved: (experimentName) => (
      initialized && (!experimentName || typeof getVariant(experimentName) !== 'undefined')
    ),
    getResolvedAddOns: () => resolvedAddOnRules.map((rule) => ({
      targetProductId: rule.targetProductId,
      targetProductHandle: rule.targetProductHandle,
      targetVariantIds: rule.targetVariantIds.slice(),
      addOnVariantIds: rule.addOnVariantIds.slice()
    })),
    getAddOnVariantIdsForVariant: (variantId) => {
      const normalizedVariantId = normalizeVariantId(variantId);
      const rule = resolvedAddOnRules.find((candidate) => candidate.targetVariantIds.includes(normalizedVariantId));
      return rule ? rule.addOnVariantIds.slice() : [];
    },
    getAlternativeVariant: (variantId) => resolvedAlternativeMap.get(normalizeVariantId(variantId)) || null
  };

  document.addEventListener('submit', prepareAlternativeSubmit, true);
  document.addEventListener('shopify:section:load', () => {
    refreshFromDom('shopify-section-load');
    processPendingTriggers();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
